/* eslint-disable react/prop-types */
import { createContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router";
import { useMsal } from "@azure/msal-react";
import * as msal from "@azure/msal-browser";
import axios from "axios";
import { WigThemeProvider } from "@warwick-investment-group/wig-react-library";
import { isTokenExpired } from "./utils/tokenUtils";
import { appInsights } from "./services/applicationInsights";
import useAppUiSettings from "./hooks/useAppUiSettings";
import { hasAdminRole } from "./config/appSupport";
import ThemePreviewNotice from "./components/themePreviewNotice";
import {
  readDensityPreference,
  resolveDensity,
  writeDensityPreference,
} from "./utils/densityPreference";

// Conditions under which acquireTokenSilent cannot recover on its own and the
// user must re-authenticate interactively (popup). InteractionRequiredAuthError
// already covers consent_required / login_required / interaction_required /
// no_tokens_found; these are the extra browser-level codes worth escalating.
// Transient failures (network, server) are intentionally absent — a popup
// won't help, so the caller should just retry.
const INTERACTIVE_ERROR_CODES = new Set([
  "monitor_window_timeout", // silent iframe blocked, typically by 3p-cookie rules
  "no_account_error", // MSAL lost the account reference
  "no_account_in_silent_request",
]);

const needsInteraction = (error) =>
  error instanceof msal.InteractionRequiredAuthError ||
  INTERACTIVE_ERROR_CODES.has(error?.errorCode);

// sessionStorage flag so a forced interactive redirect happens at most once
// per session. Prevents a redirect loop if interaction still can't mint a
// token after we come back (e.g. consent denied).
const INTERACTIVE_REDIRECT_FLAG = "msal_interactive_redirect";

export const AppContext = createContext();

export const AppContextProvider = (props) => {
  const { instance, accounts, inProgress } = useMsal();
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem("theme");
    return savedTheme || "light";
  });

  // Dev-only role override (Slice 1). Lets us exercise role gating locally,
  // since the dev token carries no BD.* App Roles. Persisted to localStorage
  // under `bd_dev_roles`. Read/applied ONLY in dev builds — see `roles` below
  // — so it is inert in production regardless of any stale localStorage value.
  const [devRoles, setDevRolesState] = useState(() => {
    try {
      const raw = localStorage.getItem("bd_dev_roles");
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });

  const setDevRoles = useCallback((next) => {
    const arr = Array.isArray(next) ? next : [];
    setDevRolesState(arr);
    try {
      localStorage.setItem("bd_dev_roles", JSON.stringify(arr));
    } catch {
      // localStorage may be unavailable (private mode); ignore.
    }
  }, []);

  // Dynamically determine the API base URL
  const isLocal = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
  const API_BASE_URL = isLocal ? "http://localhost:8000" : "http://server:8000";

  // Handle VITE_API_SCOPE as a plain string
  const apiScope = import.meta.env.VITE_API_SCOPE;
  const scopes = apiScope ? [apiScope] : [];
  if (!apiScope) {
    console.error("VITE_API_SCOPE is not set or empty. Please provide a valid scope in the format 'client-id/.default'.");
  }

  // Live mirror of MSAL's `inProgress` so async code (which closes over a
  // stale render value) can read the current interaction status.
  const inProgressRef = useRef(inProgress);
  useEffect(() => {
    inProgressRef.current = inProgress;
  }, [inProgress]);

  // Single in-flight token acquisition shared across concurrent callers.
  // Without this, every component that fetches on mount (e.g. each deal-detail
  // tab) fires its own acquireTokenSilent at once; the first to hit an
  // expired/empty cache flips `inProgress` away from "none", and the rest
  // bail to null and trip dataservice's "Token is missing" guard.
  const tokenPromiseRef = useRef(null);

  // Resolve once MSAL is idle (inProgress === "none"), or after a timeout.
  // Lets getToken WAIT through a transient interaction instead of returning
  // null the moment another caller is mid-flight.
  const waitForMsalIdle = useCallback(
    (timeoutMs = 5000) =>
      new Promise((resolve) => {
        if (inProgressRef.current === "none") {
          resolve(true);
          return;
        }
        const start = Date.now();
        const id = setInterval(() => {
          if (inProgressRef.current === "none") {
            clearInterval(id);
            resolve(true);
          } else if (Date.now() - start > timeoutMs) {
            clearInterval(id);
            resolve(false);
          }
        }, 50);
      }),
    []
  );

  const getToken = useCallback(async () => {
    // Fast path: a cached, unexpired token needs no acquisition or waiting.
    if (token && !isTokenExpired(token)) {
      return token;
    }

    // Coalesce concurrent callers onto one acquisition.
    if (tokenPromiseRef.current) {
      return tokenPromiseRef.current;
    }

    const acquire = (async () => {
      // Wait through any in-progress interaction (login redirect handling,
      // another caller's acquire) rather than bailing to null. If it never
      // settles, don't call acquireTokenSilent — it would throw
      // interaction_in_progress, and a popup would fail the same way. Defer
      // and let the caller retry once the in-flight interaction completes.
      if (inProgressRef.current !== "none") {
        const idle = await waitForMsalIdle();
        if (!idle) {
          console.warn(
            "MSAL still mid-interaction after wait; deferring token acquisition."
          );
          return null;
        }
      }

      if (!instance || accounts.length === 0) {
        console.error("MSAL instance or accounts not ready.", {
          inProgress: inProgressRef.current,
          accountsLength: accounts.length,
        });
        return null;
      }

      if (scopes.length === 0) {
        console.error("No valid scopes available for token acquisition.");
        return null;
      }

      try {
        const currentAccount = accounts[0];
        const response = await instance.acquireTokenSilent({
          scopes,
          account: currentAccount,
        });
        setToken(response.accessToken);
        sessionStorage.removeItem(INTERACTIVE_REDIRECT_FLAG);
        return response.accessToken;
      } catch (error) {
        // Escalate to interactive login whenever the silent path can't recover
        // on its own: InteractionRequiredAuthError (consent/login required, no
        // cached tokens) plus the browser-level codes in INTERACTIVE_ERROR_CODES
        // (e.g. monitor_window_timeout — the silent iframe hit a page that
        // needs UI). Transient errors (network, server) fall through to null so
        // the caller can retry without disruption.
        if (needsInteraction(error)) {
          // Use REDIRECT, not popup. These calls originate from background data
          // loads with no user gesture, so window.open is blocked
          // (popup_window_error). A full-page redirect needs no gesture and no
          // third-party cookies, so it's the only interactive path that works
          // here. Guard against loops: redirect at most once per session.
          if (sessionStorage.getItem(INTERACTIVE_REDIRECT_FLAG)) {
            sessionStorage.removeItem(INTERACTIVE_REDIRECT_FLAG);
            console.error(
              "Interactive login already attempted this session; not redirecting again.",
              error
            );
            return null;
          }
          console.warn(
            `Silent token acquisition failed (${error?.errorCode || error?.name}). ` +
            "Redirecting to login..."
          );
          sessionStorage.setItem(INTERACTIVE_REDIRECT_FLAG, "1");
          await instance.acquireTokenRedirect({ scopes, account: accounts[0] });
          return null; // page navigates away before this resolves
        }
        console.error("Unexpected token acquisition error:", error);
        return null;
      }
    })();

    tokenPromiseRef.current = acquire;
    try {
      return await acquire;
    } finally {
      tokenPromiseRef.current = null;
    }
  }, [instance, accounts, token, waitForMsalIdle]);

  const checkLocalStorage = (vname) => {
    return localStorage.getItem(vname) !== null;
  };

  const getSecret = async (secretName) => {
    let secret = null;
    try {
      const token = await getToken();
      if (!token) {
        throw new Error("Failed to acquire token for API request.");
      }

      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };
      const url = `${API_BASE_URL}/secrets`;
      const params = {
        vaultName: import.meta.env.VITE_VAULT_NAME,
        secretName: secretName,
      };
      const options = { headers, params };

      const response = await axios.get(url, options);

      if (!response.data) {
        throw new Error("API returned no data.");
      }

      secret = response.data;
    } catch (error) {
      console.error(`Error fetching secret ${secretName}:`, {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
      });
      throw error;
    }
    return secret;
  };

  const toggleTheme = () => {
    setTheme((prevTheme) => (prevTheme === "light" ? "dark" : "light"));
  };

  useEffect(() => {
    localStorage.setItem("theme", theme);
    document.body.className = `${theme}-theme`;
  }, [theme]);


  useEffect(() => {
    const updateUserState = async () => {
      const storedUser = localStorage.getItem("currentUser");

      if (inProgress !== "none") {
        if (storedUser && !user) {
          setUser(JSON.parse(storedUser));
        }
        return;
      }

      if (accounts.length > 0) {
        const account = accounts[0];
        let idTokenClaims;

        try {
          const response = await instance.acquireTokenSilent({
            scopes: ["openid", "profile", "email"],
            account,
          });
          idTokenClaims = response.idTokenClaims;
        } catch (error) {
          console.error("Failed to fetch idTokenClaims:", error);
          idTokenClaims = instance.getAccountByHomeId(account.homeAccountId)?.idTokenClaims;
          if (!idTokenClaims || Object.keys(idTokenClaims).length === 0) {
            console.warn("Cached idTokenClaims is empty; triggering interactive login...");
            try {
              const loginResponse = await instance.acquireTokenPopup({
                scopes: ["openid", "profile", "email"],
              });
              idTokenClaims = loginResponse.idTokenClaims;
            } catch (popupError) {
              console.error("Popup login failed:", popupError);
              idTokenClaims = {};
            }
          }
        }

        const userData = {
          ...account,
          idTokenClaims: idTokenClaims || {},
        };

        setUser(userData);
        localStorage.setItem("currentUser", JSON.stringify(userData));

        // Set authenticated user context for telemetry
        const userEmail = idTokenClaims?.preferred_username;
        if (userEmail) {
          appInsights?.setAuthenticatedUserContext(userEmail);
          // Clarity hashes the custom-id, so the dashboard shows an opaque
          // string unless a friendly name is supplied as the 4th argument.
          // The custom tag is what makes the recordings list filterable by user.
          const displayName = idTokenClaims?.name || account.name || userEmail;
          window.clarity?.("identify", userEmail, undefined, undefined, displayName);
          window.clarity?.("set", "userEmail", userEmail);
          window.clarity?.("set", "userName", displayName);
        }
      } else if (storedUser) {
        setUser(JSON.parse(storedUser));
        const newToken = await getToken();
        if (!newToken) {
          setUser(null);
          localStorage.removeItem("currentUser");
        }
      } else {
        setUser(null);
        localStorage.removeItem("currentUser");
      }

      setInitialized(true);

      if (accounts.length > 0 && (!token || isTokenExpired(token))) {
        const newToken = await getToken();
        if (newToken) {
          setToken(newToken);
        }
      }
    };

    updateUserState().catch((error) =>
      console.error("Error in updateUserState:", error)
    );
  }, [accounts, inProgress, getToken, token]);

  useEffect(() => {
    if (token) {
      localStorage.setItem("token", token);
    }
  }, [token]);

  useEffect(() => {
    const handleAuthChange = () => {
      if (accounts.length === 0 && inProgress === "none") {
        setUser(null);
        localStorage.removeItem("currentUser");
      }
    };
    instance.addEventCallback(handleAuthChange);
    return () => instance.removeEventCallback(handleAuthChange);
  }, [instance, accounts, inProgress]);

  // Roles from the Azure AD token's `roles` claim (App Roles); default [].
  const tokenRoles = Array.isArray(user?.idTokenClaims?.roles)
    ? user.idTokenClaims.roles
    : [];

  // In dev, a non-empty dev-override replaces the token roles for gating so
  // we can test each role locally. In production `import.meta.env.DEV` is
  // false, so the override is never consulted (and the dev control is never
  // rendered) — the token roles are authoritative.
  const roles =
    import.meta.env.DEV && devRoles.length > 0 ? devRoles : tokenRoles;

  // App-wide look and density, set from Admin → Appearance. `classic` and
  // `comfortable` (the defaults, and whenever the settings cannot be loaded)
  // draw exactly `baseTheme` below.
  const uiSettings = useAppUiSettings({
    token,
    getToken,
    isTokenExpired,
    user,
    isAdmin: hasAdminRole(roles),
  });

  // The user's own density (user menu), over the admin's app-wide default.
  const [densityPreference, setDensityPreferenceState] = useState(
    readDensityPreference
  );
  const setDensityPreference = useCallback((next) => {
    setDensityPreferenceState(next);
    writeDensityPreference(next);
  }, []);

  const baseTheme = useMemo(
    () => ({
      token: {
        // Softer corners across the app.
        borderRadius: 8,
        // The light theme read as stark: pure-white surfaces (#fff cards +
        // table bodies) glare even on a gray page. Use a neutral gray
        // palette (equal R/G/B, no blue cast) — soft off-white surfaces
        // (not clinical white), a clearly gray page so cards separate, and
        // a defined border. Dark keeps its algorithm's own colors.
        ...(theme !== "dark" && {
          colorBgContainer: "#fafafa",
          colorBgElevated: "#ffffff",
          colorBgLayout: "#dedede",
          colorBorderSecondary: "#cfcfcf",
        }),
      },
    }),
    [theme]
  );

  if (!initialized) {
    return <div>Loading...</div>;
  }

  return (
    <AppContext.Provider
      value={{
        token,
        getToken,
        ensureToken: getToken,
        isTokenExpired,
        searchParams,
        setSearchParams,
        navigate,
        location,
        checkLocalStorage,
        user,
        setUser,
        roles,
        devRoles,
        setDevRoles,
        getSecret,
        instance,
        accounts,
        theme,
        toggleTheme,
        uiSettings,
        baseTheme,
        densityPreference,
        setDensityPreference,
      }}
    >
      <WigThemeProvider
        preset={uiSettings.effectiveThemePreset}
        density={resolveDensity(densityPreference, uiSettings.density)}
        mode={theme === "dark" ? "dark" : "light"}
        base={baseTheme}
      >
        {props.children}
        <ThemePreviewNotice
          preview={uiSettings.themePreview}
          live={uiSettings.themePreset}
          onStop={uiSettings.setThemePreview}
        />
      </WigThemeProvider>
    </AppContext.Provider>
  );
};
